// Local, per-device UI state (not synced): theme, text size, sidebar, tree expansion, recents,
// system notifications (lib/notify.ts).
import { create } from 'zustand';
import type { Location } from '@shared/types';

export type RailSection = 'home' | 'spaces' | 'chat' | 'planner' | 'teams' | 'docs' | 'dashboards' | 'whiteboards' | 'forms' | 'clips' | 'timesheets' | 'goals' | 'everything';

export interface RecentItem {
  kind: 'location' | 'task' | 'doc' | 'channel';
  id: string;
  location?: Location;
  at: number;
}

// Text & icon size preference: the base (body) text size in px; other text, icons and avatars scale with it.
export const TEXT_SIZE_MIN = 12;
export const TEXT_SIZE_MAX = 18;
export const TEXT_SIZE_DEFAULT = 14;

const clampTextSize = (v: unknown) =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(TEXT_SIZE_MAX, Math.max(TEXT_SIZE_MIN, Math.round(v))) : TEXT_SIZE_DEFAULT;

interface UiState {
  theme: 'dark' | 'light' | 'system';
  textSize: number;
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  expanded: Record<string, boolean>;
  recents: RecentItem[];
  commandOpen: boolean;
  createTaskOpen: boolean;
  homeFilter: 'all' | 'unread' | 'dms';
  /** show system notifications in this browser (permission is asked separately) */
  notify: boolean;
  /** the "turn on notifications" banner was closed */
  notifyPromptDismissed: boolean;
  /** system notifications are muted until this time (ms); NOTIFY_MUTED_FOREVER = until unmuted */
  notifyMutedUntil: number | null;
  /** Personal Tools pinned to the rail (keys from shell/RailTools) */
  pinnedTools: string[];
  shortcutsOpen: boolean;
  /** Chat sidebar headings ('channels', 'dms' or a section id): what they show, how they sort, how many */
  chatSidebar: Record<string, ChatListPrefs>;
  /** statuses set lately (shell/SetStatus), newest first */
  statusRecents: StatusChoice[];
}

/** When a status clears itself (shell/SetStatus): never, after a while, at the end of today or this week. */
export type StatusClear = 'none' | '30m' | '1h' | '2h' | '4h' | 'today' | 'week';

export interface StatusChoice {
  emoji: string;
  text: string;
  clear: StatusClear;
}

export interface ChatListPrefs {
  show?: 'all' | 'unread';
  sort?: 'alpha' | 'recent';
  /** show only this many (most recent first when sorted by activity); unread and open ones always show */
  limit?: number | null;
}

export const NOTIFY_MUTED_FOREVER = Number.MAX_SAFE_INTEGER;
export const notificationsMuted = (s: { notifyMutedUntil: number | null }) => (s.notifyMutedUntil ?? 0) > Date.now();

const KEY = 'atrium.ui';

function load(): Partial<UiState> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}');
  } catch {
    return {};
  }
}

const saved = load();

export const useUi = create<UiState>(() => ({
  theme: saved.theme ?? 'dark',
  textSize: clampTextSize(saved.textSize),
  sidebarCollapsed: saved.sidebarCollapsed ?? false,
  sidebarWidth: saved.sidebarWidth ?? 288,
  expanded: saved.expanded ?? {},
  recents: saved.recents ?? [],
  commandOpen: false,
  createTaskOpen: false,
  homeFilter: 'all',
  notify: saved.notify ?? true,
  notifyPromptDismissed: saved.notifyPromptDismissed ?? false,
  notifyMutedUntil: saved.notifyMutedUntil ?? null,
  pinnedTools: saved.pinnedTools ?? [],
  shortcutsOpen: false,
  chatSidebar: saved.chatSidebar ?? {},
  statusRecents: saved.statusRecents ?? [],
}));

let lastWritten = '';
useUi.subscribe((s) => {
  try {
    const { theme, textSize, sidebarCollapsed, sidebarWidth, expanded, recents, notify, notifyPromptDismissed, notifyMutedUntil, pinnedTools, chatSidebar, statusRecents } = s;
    const json = JSON.stringify({ theme, textSize, sidebarCollapsed, sidebarWidth, expanded, recents, notify, notifyPromptDismissed, notifyMutedUntil, pinnedTools, chatSidebar, statusRecents });
    if (json === lastWritten) return;
    lastWritten = json;
    localStorage.setItem(KEY, json);
  } catch {
    /* ignore */
  }
});

// Keep tabs/windows in sync: another tab writing the key updates this one.
// (Writing an unchanged value doesn't fire 'storage', so this can't ping-pong.)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY || !e.newValue) return;
    try {
      if (e.newValue === lastWritten) return;
      const next = JSON.parse(e.newValue) as Partial<UiState>;
      const cur = useUi.getState();
      const patch: Partial<UiState> = {};
      // only adopt fields that actually differ, so a sync never causes a rewrite loop
      if (next.theme && next.theme !== cur.theme) patch.theme = next.theme;
      if (next.textSize !== undefined && clampTextSize(next.textSize) !== cur.textSize) patch.textSize = clampTextSize(next.textSize);
      if (next.sidebarCollapsed !== undefined && next.sidebarCollapsed !== cur.sidebarCollapsed) patch.sidebarCollapsed = next.sidebarCollapsed;
      if (next.sidebarWidth && next.sidebarWidth !== cur.sidebarWidth) patch.sidebarWidth = next.sidebarWidth;
      if (next.expanded && JSON.stringify(next.expanded) !== JSON.stringify(cur.expanded)) patch.expanded = next.expanded;
      if (next.recents && JSON.stringify(next.recents) !== JSON.stringify(cur.recents)) patch.recents = next.recents;
      if (next.notify !== undefined && next.notify !== cur.notify) patch.notify = next.notify;
      if (next.notifyPromptDismissed !== undefined && next.notifyPromptDismissed !== cur.notifyPromptDismissed) patch.notifyPromptDismissed = next.notifyPromptDismissed;
      if (next.notifyMutedUntil !== undefined && next.notifyMutedUntil !== cur.notifyMutedUntil) patch.notifyMutedUntil = next.notifyMutedUntil;
      if (next.pinnedTools && JSON.stringify(next.pinnedTools) !== JSON.stringify(cur.pinnedTools)) patch.pinnedTools = next.pinnedTools;
      if (next.chatSidebar && JSON.stringify(next.chatSidebar) !== JSON.stringify(cur.chatSidebar)) patch.chatSidebar = next.chatSidebar;
      if (next.statusRecents && JSON.stringify(next.statusRecents) !== JSON.stringify(cur.statusRecents)) patch.statusRecents = next.statusRecents;
      lastWritten = e.newValue;
      if (Object.keys(patch).length) useUi.setState(patch);
    } catch {
      /* ignore malformed */
    }
  });
}

export function setChatListPrefs(key: string, changes: ChatListPrefs) {
  useUi.setState((s) => ({ chatSidebar: { ...s.chatSidebar, [key]: { ...s.chatSidebar[key], ...changes } } }));
}

export function toggleExpanded(key: string, value?: boolean) {
  useUi.setState((s) => ({ expanded: { ...s.expanded, [key]: value ?? !s.expanded[key] } }));
}

export function pushStatusRecent(choice: StatusChoice) {
  const same = (c: StatusChoice) => c.emoji === choice.emoji && c.text === choice.text;
  useUi.setState((s) => (s.statusRecents[0] && same(s.statusRecents[0]) && s.statusRecents[0].clear === choice.clear ? s : { statusRecents: [choice, ...s.statusRecents.filter((c) => !same(c))].slice(0, 3) }));
}

export function pushRecent(item: Omit<RecentItem, 'at'>) {
  // idempotent: re-visiting the item that's already most recent within a minute is a no-op
  const top = useUi.getState().recents[0];
  if (top && top.kind === item.kind && top.id === item.id && Date.now() - top.at < 60_000) return;
  useUi.setState((s) => ({
    recents: [{ ...item, at: Date.now() }, ...s.recents.filter((r) => !(r.kind === item.kind && r.id === item.id))].slice(0, 30),
  }));
}

export function applyTheme(theme: UiState['theme']) {
  const resolved = theme === 'system' ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : theme;
  document.documentElement.dataset.theme = resolved;
  // desktop app on Windows: the window buttons sit on the top bar, so give them its colors
  const bridge = window.atriumDesktop;
  if (bridge?.setTitleBarColors) {
    const css = getComputedStyle(document.documentElement);
    bridge.setTitleBarColors({ color: css.getPropertyValue('--bg-chrome').trim(), symbolColor: css.getPropertyValue('--text-1').trim() });
  }
}

export function setTextSize(px: number) {
  useUi.setState({ textSize: clampTextSize(px) });
}

export function applyTextSize(px: number) {
  document.documentElement.style.setProperty('--text-scale', String(clampTextSize(px) / TEXT_SIZE_DEFAULT));
}
